import type { ReactNode } from 'react';
import { ChevronRightIcon } from '@heroicons/react/24/outline';

interface AccessCardSubsectionProps {
    label: ReactNode;
    /** Small trailing meta next to the label (a count, a qualifier like "grantable"). */
    count?: ReactNode;
    /** A top border before this subsection - every one but the first. */
    divider?: boolean;
    children: ReactNode;
    /** Toggle button with a rotating chevron, gates `children` behind `open`. False by default. */
    collapsible?: boolean;
    /** Only meaningful when `collapsible` is set. Defaults to true (expanded). */
    open?: boolean;
    onToggle?: () => void;
}

// Labelled subsection within an AccessCard body; label carries its own
// px-3.5 to line up with row content in `noBodyPadding` cards.
export function AccessCardSubsection({
    label,
    count,
    divider = false,
    children,
    collapsible = false,
    open = true,
    onToggle
}: AccessCardSubsectionProps) {
    const showChildren = !collapsible || open;

    return (
        <div className={`flex flex-col ${divider ? 'border-t border-gray-100' : ''}`.trim()}>
            {collapsible ? (
                <button
                    type="button"
                    onClick={onToggle}
                    aria-expanded={open}
                    className="flex items-center gap-1.5 px-3.5 pt-3 pb-1.5 w-full text-left"
                >
                    <span className="text-[11px] font-bold text-gray-400 uppercase tracking-[0.04em] flex-1">
                        {label}
                    </span>
                    {count !== undefined && (
                        <span className="text-[11px] text-gray-400 normal-case">{count}</span>
                    )}
                    <ChevronRightIcon
                        className={`h-3 w-3 text-gray-400 shrink-0 transition-transform duration-150 ${
                            open ? 'rotate-90' : ''
                        }`}
                    />
                </button>
            ) : (
                <div className="flex items-center gap-1.5 px-3.5 pt-3 pb-1.5">
                    <span className="text-[11px] font-bold text-gray-400 uppercase tracking-[0.04em]">
                        {label}
                    </span>
                    {count !== undefined && (
                        <span className="text-[11px] text-gray-400 normal-case">{count}</span>
                    )}
                </div>
            )}
            {showChildren && children}
        </div>
    );
}
